import { useState } from "react";
import { format } from "date-fns";
import { Avatar, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Navigation, Pagination, Mousewheel, Keyboard } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Button, DataDialog, Video } from "@app/components/atoms";
import CustomImageViewer from "@app/components/atoms/CustomImageViewer/CustomImageViewer";
import { IconInvalidUrl, IconUser } from "@app/components/icons";
import { INVALID_URL } from "@app/constants";

type MachineReportOption = {
  name?: string;
  selected?: boolean;
};

export type MachineReportDetailData = {
  reportTitle?: string;
  reportedAt?: string;
  freeText?: string | null;
  options?: MachineReportOption[] | null;
  machineReportResponses?: Array<{
    reportComment?: string | null;
    userPictureUrl?: string | null;
    givenName?: string;
    surname?: string;
    machineReportMedias?: Array<{
      mediaUrl?: string;
      mimeType?: string;
      captureMediaAt?: string;
    }>;
    machineReportRecordings?: Array<{
      recordingUrl?: string;
    }>;
  }>;
};

type MachineReportDetailDialogProps = {
  open: boolean;
  onClose: () => void;
  dbMachine?: MachineReportDetailData | null;
  setDbMachine: (value: MachineReportDetailData | "" | null) => void;
  userTimeFormat: string;
  localeCode: string;
};

function resolveMachineReportDisplayComment(
  reportItem: { reportComment?: string | null } | null | undefined,
  machineReport: MachineReportDetailData | null | undefined,
): string {
  const reportComment = reportItem?.reportComment;
  if (typeof reportComment === "string" && reportComment.trim()) {
    return reportComment.trim();
  }

  const freeText = machineReport?.freeText;
  if (typeof freeText === "string" && freeText.trim()) {
    return freeText.trim();
  }

  const selectedOptionName = machineReport?.options?.find(
    (option) => option.selected,
  )?.name;
  if (typeof selectedOptionName === "string" && selectedOptionName.trim()) {
    return selectedOptionName.trim();
  }

  return "";
}

export function MachineReportDetailDialog({
  open,
  onClose,
  dbMachine,
  setDbMachine,
  userTimeFormat,
  localeCode,
}: MachineReportDetailDialogProps) {
  const { t } = useTranslation();
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const handleMediaOnError = (index: number, mediaIndex: number) => {
    if (!dbMachine?.machineReportResponses) {
      return;
    }

    const newDbMachine = {
      ...dbMachine,
      machineReportResponses: [...dbMachine.machineReportResponses],
    };
    const medias = [
      ...(newDbMachine.machineReportResponses[index].machineReportMedias ?? []),
    ];
    if (medias[mediaIndex]) {
      medias[mediaIndex] = {
        ...medias[mediaIndex],
        mediaUrl: INVALID_URL,
      };
    }
    newDbMachine.machineReportResponses[index] = {
      ...newDbMachine.machineReportResponses[index],
      machineReportMedias: medias,
    };
    setDbMachine(newDbMachine);
  };

  if (!open || !dbMachine?.machineReportResponses?.length) {
    return null;
  }

  return (
    <>
      <DataDialog
        title={dbMachine.reportTitle}
        disableBackdropClick={true}
        open={open}
        setOpen={onClose}
        className="dialog-report dialog-machine-report"
        dialogActions={
          <div className="flex justify-content-end">
            <Button onClick={onClose} className="btn btn-no-border">
              {t("dialogRefuel.close")}
            </Button>
          </div>
        }
      >
        {dbMachine.machineReportResponses.map((item, index) => {
          const displayComment = resolveMachineReportDisplayComment(
            item,
            dbMachine,
          );

          return (
            <div
              className="content pb-0 pt-0 ml-24 mr-24 bg-white"
              key={index}
            >
              <div className="report-card">
                <div className="content-report border-0">
                  {!!item?.machineReportMedias?.length && (
                    <Swiper
                      cssMode={true}
                      navigation={true}
                      pagination={true}
                      mousewheel={true}
                      keyboard={true}
                      modules={[Navigation, Pagination, Mousewheel, Keyboard]}
                      className="swiper mb-16"
                    >
                      {item.machineReportMedias.map((media, mediaIndex) => (
                        <SwiperSlide key={mediaIndex}>
                          <div className="wrapper-file">
                            {media?.mediaUrl === INVALID_URL ? (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <IconInvalidUrl />
                              </Box>
                            ) : media?.mimeType?.includes("video") ? (
                              <Video
                                media={media}
                                onError={() =>
                                  handleMediaOnError(index, mediaIndex)
                                }
                              />
                            ) : (
                              <div
                                className="wrapper-file"
                                onClick={() => {
                                  if (media.mediaUrl) {
                                    setSelectedImage(media.mediaUrl);
                                    setViewerOpen(true);
                                  }
                                }}
                                style={{ cursor: "zoom-in" }}
                              >
                                <Box
                                  className="swiper-image img-report"
                                  component="img"
                                  alt="swiper-image"
                                  src={media.mediaUrl}
                                  onError={() =>
                                    handleMediaOnError(index, mediaIndex)
                                  }
                                />
                              </div>
                            )}
                          </div>
                          <p
                            className="created-at txt-gray-1 fs-12"
                            style={{
                              textAlign: "right",
                              marginRight: "10px",
                              fontWeight: "bold",
                            }}
                          >
                            {media.captureMediaAt ? (
                              isNaN(
                                new Date(media.captureMediaAt).getTime(),
                              ) ? (
                                <span style={{ color: "red" }}></span>
                              ) : (
                                `${format(
                                  new Date(
                                    new Date(media.captureMediaAt).toLocaleString(
                                      "en-US",
                                      {
                                        timeZone:
                                          Intl.DateTimeFormat().resolvedOptions()
                                            .timeZone,
                                      },
                                    ),
                                  ),
                                  userTimeFormat,
                                )}`
                              )
                            ) : (
                              ""
                            )}
                          </p>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  )}

                  {displayComment ? (
                    <p className="fs-16 mt-0 txt-black txt-pre-wrap word-break">
                      {displayComment}
                    </p>
                  ) : null}

                  {!!item?.machineReportRecordings?.length && (
                    <div style={{ marginTop: "12px" }}>
                      {item.machineReportRecordings.map((recording, rIndex) => (
                        <div
                          key={rIndex}
                          style={{
                            marginBottom: "16px",
                          }}
                        >
                          <audio
                            controls
                            src={recording.recordingUrl}
                            style={{ width: "100%" }}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="fs-14 txt-gray-1 m-0">
                    {t("tableMachineDetail.reporter")}
                  </p>
                  <div className="flex-centered justify-content-start mt-8">
                    {!!item?.userPictureUrl ? (
                      item.userPictureUrl === INVALID_URL ? (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <IconInvalidUrl />
                        </Box>
                      ) : (
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                          }}
                          alt="avatar-reporter"
                          src={item.userPictureUrl}
                          imgProps={{
                            onError: () => {
                              const newDbMachine = {
                                ...dbMachine,
                                machineReportResponses: [
                                  ...dbMachine.machineReportResponses!,
                                ],
                              };
                              newDbMachine.machineReportResponses[index] = {
                                ...newDbMachine.machineReportResponses[index],
                                userPictureUrl: INVALID_URL,
                              };
                              setDbMachine(newDbMachine);
                            },
                          }}
                        />
                      )
                    ) : (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <IconUser />
                      </Box>
                    )}

                    <span className="fs-16 txt-black-1 ml-5 word-break">
                      {localeCode === "ja"
                        ? `${item?.surname} ${item?.givenName}`
                        : `${item?.givenName} ${item?.surname}`}
                    </span>
                  </div>
                  <p className="fs-14 txt-gray-1 mt-16 mb-0">
                    {t("tableMachineDetail.date")}
                  </p>
                  <p className="fs-16 txt-black-1 mt-5 mb-0">
                    {dbMachine.reportedAt &&
                      format(new Date(dbMachine.reportedAt), userTimeFormat)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </DataDialog>

      {viewerOpen && (
        <CustomImageViewer
          imageUrl={selectedImage}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </>
  );
}
